import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useAuth } from './auth';
import { SUBSCRIPTION_QUERY } from '../graphql/queries';
import { mapStripePricesToPlans } from '../utils';
import Stripe from 'stripe';
import { STRIPE_SUBSCRIPTION_ACTIVE_STATUSES } from '../lib/constants';
import { UNLIMITED_SPARK_LIMIT } from './tokens';

const SubscriptionContext = createContext<any>(undefined);

// Ascending tier order — 'pro' is the highest tier. Must match Stripe product names exactly.
const PLAN_TIER = ['growth', 'pro'];

// Exact Stripe product names that are core subscription plans.
// Add-ons ("Follower Growth Annual/Monthly", "Art Marketing Autopilot Annual/Monthly",
// "Human Made Art") are intentionally excluded.
const CORE_PLAN_NAMES = new Set(['free', 'growth', 'pro']);

function isCorePlanSubscription(s: any): boolean {
  const name: string = s?.plan?.product?.name?.toLowerCase() ?? '';
  return CORE_PLAN_NAMES.has(name);
}

function getActiveCoreSubscription(data: Stripe.Subscription[]): Stripe.Subscription | undefined {
  return data
    .filter((s: any) => ['active', 'trialing'].includes(s.status) && isCorePlanSubscription(s))
    .sort((a: any, b: any) => {
      const nameA: string = a?.plan?.product?.name?.toLowerCase() ?? '';
      const nameB: string = b?.plan?.product?.name?.toLowerCase() ?? '';
      const tierA = PLAN_TIER.findIndex((t) => nameA.includes(t));
      const tierB = PLAN_TIER.findIndex((t) => nameB.includes(t));
      return tierB - tierA; // highest tier first
    })[0];
}

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { isLoaded: clerkLoaded, isSignedIn: clerkSignedIn } = useClerkAuth();

  // SUBSCRIPTION_QUERY is skipped until our own user row is mapped, and Apollo
  // reports a skipped query as `loading: false` — so that window would otherwise
  // read as a settled "no subscription" and bounce a paying user off /billing.
  // `AuthProvider` cannot report it: it renders a spinner in place of its children
  // while its own `loading` is true, so descendants never observe that flag.
  // Signed out, Clerk settles and this stays false rather than loading forever.
  const userUnresolved = !clerkLoaded || (Boolean(clerkSignedIn) && !user?.user_id);

  const { data, loading, error } = useQuery(SUBSCRIPTION_QUERY, {
    variables: { asfId: user?.user_id },
    fetchPolicy: 'network-only',
    skip: !user?.user_id,
  });

  const subscriptions = data?.user?.subscriptions;

  let subscription: any;
  let hasSubscription = false;
  let isActiveSubscriptionsLimitReached = false;

  if (subscriptions?.data?.length > 0) {
    const subscriptionsData: Stripe.Subscription[] = subscriptions.data;

    subscription = getActiveCoreSubscription(subscriptionsData);

    // Any active/trialing subscription (including HMA) counts — HMA-only users
    // must not be locked out of the billing page.
    hasSubscription = subscriptionsData.some((s: any) => ['active', 'trialing'].includes(s.status));

    isActiveSubscriptionsLimitReached = subscriptionsData.some((subscription) =>
      (Object.values(STRIPE_SUBSCRIPTION_ACTIVE_STATUSES) as string[]).includes(subscription.status)
    );
  }

  const plans = useMemo(() => {
    if (!data?.prices) return [];
    const currentPlan = subscription?.plan?.product?.name;
    return mapStripePricesToPlans(data.prices, currentPlan);
  }, [data?.prices, subscription]);

  const social_post_limit = useMemo(() => {
    if (!data?.prices) return UNLIMITED_SPARK_LIMIT;

    const socialPostLimit = subscription?.plan?.product?.metadata?.social_post_limit;
    if (socialPostLimit === UNLIMITED_SPARK_LIMIT) return UNLIMITED_SPARK_LIMIT;

    const socialPostLimitNumber = Number(socialPostLimit);
    if (socialPostLimit !== null && socialPostLimit !== '' && !Number.isNaN(socialPostLimitNumber))
      return socialPostLimitNumber;

    return __DEFAULT_SOCIAL_POST_LIMIT__;
  }, [data?.prices, subscription]);

  const spark_limit = useMemo(() => {
    if (!data?.prices) return UNLIMITED_SPARK_LIMIT;

    const sparkLimit = subscription?.plan?.product?.metadata?.spark_limit;
    if (sparkLimit === UNLIMITED_SPARK_LIMIT) return UNLIMITED_SPARK_LIMIT;

    const sparkLimitNumber = Number(sparkLimit);
    if (sparkLimit !== null && sparkLimit !== '' && !Number.isNaN(sparkLimitNumber)) return sparkLimitNumber;

    // No subscription (free plan) → no cap. Monthly spark credits removed for
    // free users 2026-08; a numeric cap now only applies when a plan's Stripe
    // product metadata explicitly sets one.
    return UNLIMITED_SPARK_LIMIT;
  }, [data?.prices, subscription]);

  const hasPlan = useCallback(
    (planNames: string) => {
      if (!subscriptions?.data?.length) return false;
      const names = planNames.split(',').map((n: string) => n.trim().toLowerCase());
      return subscriptions.data.some((s: any) => {
        if (!['active', 'trialing'].includes(s.status)) return false;
        const productName: string = s?.plan?.product?.name?.toLowerCase() ?? '';
        if (!isCorePlanSubscription(s)) return false;
        return names.some((name: string) => productName.includes(name));
      });
    },
    [subscriptions]
  );

  const humanMadeArtSubscriptionStatus = data?.user?.humanMadeArtSubscriptionStatus ?? null;

  const value = {
    subscriptions,
    subscription,
    plans,
    hasSubscription,
    hasPlan,
    spark_limit,
    social_post_limit,
    loading: loading || userUnresolved,
    error,
    isActiveSubscriptionsLimitReached,
    humanMadeArtSubscriptionStatus,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = (): any => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within an SubscriptionProvider');
  }
  return context;
};
